import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, ASSET_BASE } from "@/lib/api";
import { PHOTOS } from "@/lib/mediaData";
import { PlatformIcon } from "@/lib/platformIcons";
import { responsiveImage, heroPreloadAttrs } from "@/lib/mediaImages";
import { useMediaContent } from "@/lib/useMediaContent";
import "./MediaCenter.css";

// Lightweight, privacy-safe outbound-click tracker. Persists via
// `/api/log/client` when the sink is available; falls back to a
// no-op. Fires for press, social, downloads, email, phone, telegram.
// The active locale is attached to every event so we can slice Arabic
// vs English activity in analytics without a second pipeline.
function useTrack() {
  const { lang } = useMediaContent();
  return (event, context = {}) => {
    try {
      api.post("/log/client", {
        event: `media_${event}`,
        context: { ...context, locale: lang },
        url: window.location.href,
        ua: navigator.userAgent,
        ts: new Date().toISOString(),
      }).catch(() => {});
    } catch (_) { /* never block UX on analytics */ }
  };
}

/* --------------------------------------------------------------------------
 * DJ SPY OFFICIAL MEDIA CENTER — /media  (EN)  and  /ar/media  (AR, RTL)
 * Editorial newsroom. Sixteen sections.
 * Strings routed through useMediaContent() which merges the English
 * source-of-truth in `mediaData.js` with the Arabic overrides in
 * `mediaDataAr.js`. Anything missing on the AR side falls back to EN.
 * -------------------------------------------------------------------------- */


function MediaNav() {
  const { ui, isRTL, lang } = useMediaContent();
  const track = useTrack();
  const [active, setActive] = useState("");
  const sections = useMemo(() => {
    const all = [
      { id: "biography",     label: ui.navLabels.biography },
      { id: "career",        label: ui.navLabels.career },
      { id: "numbers",       label: ui.navLabels.numbers },
      { id: "presence",      label: ui.navLabels.presence },
      { id: "recognition",   label: ui.navLabels.recognition },
      { id: "press",         label: ui.navLabels.press },
      { id: "gallery",       label: ui.navLabels.gallery },
      { id: "downloads",     label: ui.navLabels.downloads },
      { id: "bookings",      label: ui.navLabels.bookings },
    ];
    // Collapse consecutive duplicates — happens in AR where "الأرقام
    // والمحطات" intentionally covers both #numbers and #recognition.
    const seen = new Set();
    return all.filter((s) => {
      if (seen.has(s.label)) return false;
      seen.add(s.label);
      return true;
    });
  }, [ui]);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
    }, { rootMargin: "-40% 0px -55% 0px" });
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [sections]);
  // Language switch — hops between /media and /ar/media, preserving the
  // hash so the reader lands on the same section in the other language.
  const otherLocaleHref = lang === "ar" ? "/media" : "/ar/media";
  return (
    <nav className="media-nav" data-testid="media-nav">
      <div className="media-nav-inner">
        <Link to={lang === "ar" ? "/ar" : "/"} className="media-nav-brand" data-testid="media-brand">
          DJ <strong>SPY</strong> <span style={{fontSize:11,letterSpacing:'0.3em',marginInlineStart:12,color:'var(--media-faint)'}}>{ui.siteName}</span>
        </Link>
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`} className={active === s.id ? "active" : ""}
             data-testid={`media-nav-${s.id}`}>
            {s.label}
          </a>
        ))}
        <Link to={otherLocaleHref}
              className="media-lang-switch"
              data-testid="media-lang-switch"
              onClick={() => track("language_switch_click", { to: lang === "ar" ? "en" : "ar" })}
              aria-label={lang === "ar" ? "Switch to English" : "Switch to Arabic"}>
          {ui.languageSwitch}
        </Link>
      </div>
    </nav>
  );
}


function Hero() {
  const { ui, HERO_STATS } = useMediaContent();
  const track = useTrack();
  const heroImg = responsiveImage(PHOTOS.heroPortrait, "100vw");
  return (
    <header className="media-hero" data-testid="media-hero">
      <img
        className="media-hero-img"
        src={heroImg.src}
        srcSet={heroImg.srcSet || undefined}
        sizes={heroImg.sizes || undefined}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
      />
      <div className="media-hero-scrim" aria-hidden="true" />
      <div className="media-hero-content">
        <div className="media-eyebrow">{ui.sections.s01_hero}</div>
        <h1 className="media-display" style={{ marginTop: 24 }}>DJ SPY</h1>
        <div className="media-hero-sub">
          {ui.hero.roles.map((r, i) => (
            <span key={r}>{r}{i < ui.hero.roles.length - 1 ? " · " : ""}</span>
          ))}
        </div>
        <div className="media-hero-cta">
          <a href="#downloads" className="media-btn media-btn--accent"
             data-testid="hero-view-media-assets"
             onClick={() => track("view_media_assets_click")}>
            {ui.ctaViewAssets}
          </a>
          <a href="#bookings" className="media-btn"
             data-testid="hero-media-contact"
             onClick={() => track("hero_media_contact_click")}>
            {ui.ctaMediaContact}
          </a>
        </div>
        <div className="media-hero-stats">
          {HERO_STATS.map((s) => (
            <div key={s.label}>
              <div className="media-stat-value">{s.value}</div>
              <div className="media-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}


function Biography() {
  const { ui, BIO_PARAGRAPHS, QUICK_FACTS } = useMediaContent();
  return (
    <section id="biography" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s02_bio_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s02_bio_title}</h2>
        </div>
        <div className="media-bio">
          <div className="media-bio-body">
            {BIO_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
            <div className="media-fact-card" data-testid="quick-facts">
              <div className="media-eyebrow" style={{marginBottom:20}}>{ui.labels.quickFacts}</div>
              <dl>
                {QUICK_FACTS.map((f) => (
                  <div key={f.label}>
                    <dt>{f.label}</dt>
                    <dd>{f.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <div className="media-bio-portrait">
            {(() => {
              const r = responsiveImage(PHOTOS.bioPortrait, "(max-width: 900px) 100vw, 45vw");
              return (
                <img
                  src={r.src}
                  srcSet={r.srcSet || undefined}
                  sizes={r.sizes || undefined}
                  alt="DJ SPY"
                  loading="lazy"
                  decoding="async"
                />
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}


function CareerTimeline() {
  const { ui, TIMELINE, TIMELINE_DISCLAIMER } = useMediaContent();
  return (
    <section id="career" className="media-section">
      <div className="media-narrow">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s03_timeline_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s03_timeline_title}</h2>
          <p className="media-lede">{ui.sections.s03_timeline_lede}</p>
        </div>
        <div className="media-timeline" data-testid="timeline">
          {TIMELINE.map((t, i) => (
            <div key={i} className="media-timeline-item">
              <div className="media-timeline-year">{t.year}</div>
              <div className="media-timeline-title">{t.title}</div>
              <div className="media-timeline-copy">{t.copy}</div>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 32, color: "var(--media-faint)", fontSize: 12, letterSpacing: "0.08em", fontStyle: "italic" }} data-testid="timeline-disclaimer">
          {TIMELINE_DISCLAIMER}
        </p>
      </div>
    </section>
  );
}


function ByTheNumbers() {
  const { ui, NUMBERS, STATS_DISCLAIMER } = useMediaContent();
  return (
    <section id="numbers" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s04_numbers_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s04_numbers_title}</h2>
        </div>
        <div className="media-numbers" data-testid="numbers">
          {NUMBERS.map((n, i) => (
            <div key={`${n.label}-${i}`} className="media-number-card">
              <div className="value">{n.value}</div>
              <div className="label">{n.label}</div>
              {n.copy ? <div className="copy" style={{ marginTop: 8, fontSize: 12, color: "var(--media-mute)" }}>{n.copy}</div> : null}
            </div>
          ))}
        </div>
        <p style={{ marginTop: 32, color: "var(--media-faint)", fontSize: 12, letterSpacing: "0.08em", fontStyle: "italic" }} data-testid="stats-disclaimer">
          {STATS_DISCLAIMER}
        </p>
      </div>
    </section>
  );
}


// Resolves a Trusted By logo_url that may be either an absolute CDN URL
// (uploaded photo) or a backend-relative path like `/api/trusted-by/venues/{id}/logo`.
function resolveVenueLogo(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${ASSET_BASE}${u}`;
}


function InternationalPresence() {
  // Trusted-By admin panel is the source of truth for venues in BOTH
  // locales. When the admin adds `country_ar`/`city_ar` fields we'll
  // read those preferentially; today we fall back to a controlled
  // mapping in `mediaDataAr.js` so we never invent Arabic names.
  const { ui, venueLoc, isAr } = useMediaContent();
  const track = useTrack();
  const [venues, setVenues] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/trusted-by");
        if (cancelled) return;
        const items = Array.isArray(res.data?.venues) ? res.data.venues : [];
        setVenues(items);
      } catch (_) {
        if (!cancelled) { setFailed(true); setVenues([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pick a locale-appropriate name/city/country. Backend `*_ar` fields
  // win when supplied; otherwise the English value shines through
  // (venue names, in particular, stay unchanged unless the admin has
  // typed an approved Arabic form).
  const loc = (v) => {
    const city = isAr ? (v.city_ar || venueLoc.cities[v.city] || v.city) : v.city;
    const country = isAr ? (v.country_ar || venueLoc.countries[v.country] || v.country) : v.country;
    return { city, country };
  };
  const displayName = (v) => (isAr ? (v.name_ar || v.name) : v.name);

  const countries = useMemo(() => {
    if (!venues) return [];
    const byCountry = new Map();
    for (const v of venues) {
      const { country } = loc(v);
      if (!country) continue;
      if (!byCountry.has(country)) byCountry.set(country, []);
      byCountry.get(country).push(displayName(v));
    }
    return Array.from(byCountry, ([name, names]) => ({ name, venues: names }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, isAr]);

  const loading = venues === null;
  const empty   = !loading && venues.length === 0;

  return (
    <section id="presence" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s05_presence_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s05_presence_title}</h2>
          <p className="media-lede">{ui.sections.s05_presence_lede}</p>
        </div>
        <div className="media-presence">
          <div className="media-countries" data-testid="countries">
            {countries.map((c) => (
              <div key={c.name} className="media-country">
                <strong>{c.name}</strong>
                <span>{c.venues.join(" · ")}</span>
              </div>
            ))}
          </div>
          <div className="media-venue-grid" data-testid="venue-grid">
            {loading && Array.from({ length: 6 }).map((_, i) => (
              <div key={`sk-${i}`} className="media-venue" aria-hidden="true" data-testid="venue-skeleton" />
            ))}
            {empty && (
              <div className="media-venue" data-testid="venue-empty"
                   style={{ gridColumn: "1 / -1", flexDirection: "column", gap: 6 }}>
                <div className="media-eyebrow" style={{ color: "var(--media-accent)" }}>
                  {failed ? ui.labels.unavailable : ui.labels.noVenues}
                </div>
                <div style={{ color: "var(--media-mute)", fontSize: 13, textAlign: "center" }}>
                  {failed ? ui.labels.unavailableCopy : ui.labels.noVenuesCopy}
                </div>
              </div>
            )}
            {!loading && !empty && venues.map((v) => {
              const src = resolveVenueLogo(v.logo_url);
              const name = displayName(v);
              const { city, country } = loc(v);
              const alt = v.alt_text || `${name} logo`;
              const locLine = [city, country].filter(Boolean).join(" · ");
              const hasWebsite = !!v.website_url && v.open_website_on_click !== false;
              const testId = `venue-${(v.id || v.name).toString().replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;

              const inner = src ? (
                <>
                  <img src={src} alt={alt} loading="lazy" decoding="async" />
                  {name && <div className="media-venue-name">{city ? `${name} · ${city}` : name}</div>}
                </>
              ) : (
                <div style={{
                  width: "100%", height: "100%",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  background: "linear-gradient(135deg, #0e0e0e 0%, #141414 100%)",
                  color: "var(--media-mute)", padding: 20, textAlign: "center",
                }} aria-label={locLine ? `${name} — ${locLine}` : name}>
                  <div className="media-eyebrow" style={{ color: "var(--media-accent)", marginBottom: 8 }}>{ui.labels.featuredVenue}</div>
                  <div style={{ fontFamily: '"Bebas Neue", "IBM Plex Sans Arabic", sans-serif', fontSize: 22, letterSpacing: "0.06em", color: "var(--media-ink)" }}>{name}</div>
                  {locLine && <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", marginTop: 4 }}>{locLine}</div>}
                </div>
              );

              return hasWebsite ? (
                <a key={v.id || v.name}
                   href={v.website_url}
                   target="_blank"
                   rel="noopener noreferrer"
                   aria-label={`Visit ${name} website`}
                   className="media-venue"
                   data-testid={testId}
                   onClick={() => track("venue_link_click", { venue: name })}>
                  {inner}
                </a>
              ) : (
                <div key={v.id || v.name} className="media-venue" data-testid={testId}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}


function PerformedAlongside() {
  const { ui, ALONGSIDE, ALONGSIDE_DISCLAIMER } = useMediaContent();
  return (
    <section className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s06_alongside_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s06_alongside_title}</h2>
        </div>
        <div className="media-alongside" data-testid="alongside">
          {ALONGSIDE.map((a) => (
            <div key={a} className="media-alongside-card">
              <div className="name">{a}</div>
            </div>
          ))}
        </div>
        <div className="media-alongside-disclaimer" data-testid="alongside-disclaimer">
          {ALONGSIDE_DISCLAIMER}
        </div>
      </div>
    </section>
  );
}


function TheMusic() {
  const { ui, GENRES } = useMediaContent();
  return (
    <section className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s07_music_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s07_music_title}</h2>
          <p className="media-lede">{ui.sections.s07_music_lede}</p>
        </div>
        <div className="media-genres" data-testid="genres">
          {GENRES.map((g) => <div key={g} className="media-genre-pill">{g}</div>)}
        </div>
        <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
          <div>
            <div className="media-eyebrow">{ui.sections.s07_music_output_eyebrow}</div>
            <h3 className="media-display" style={{ fontSize: 40, marginTop: 8 }}>
              {ui.sections.s07_music_output_title}
            </h3>
            <p className="media-lede" style={{ marginTop: 20, fontSize: 16 }}>
              {ui.sections.s07_music_output_copy}
            </p>
          </div>
          <div style={{ aspectRatio: "4/5", overflow: "hidden" }}>
            {(() => {
              const r = responsiveImage(PHOTOS.studioPortrait, "(max-width: 900px) 100vw, 45vw");
              return (
                <img
                  src={r.src}
                  srcSet={r.srcSet || undefined}
                  sizes={r.sizes || undefined}
                  alt="DJ SPY"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  loading="lazy"
                  decoding="async"
                />
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}


function TheVision() {
  const { ui, VISION_HEADLINE, VISION_PARAGRAPHS } = useMediaContent();
  return (
    <section className="media-section">
      <div className="media-narrow">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s08_vision_eyebrow}</div>
          <h2 className="media-display">{VISION_HEADLINE}</h2>
        </div>
        <div className="media-vision-article" data-testid="vision">
          {VISION_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
        </div>
        <div style={{ margin: "56px 0", aspectRatio: "3/2", overflow: "hidden" }}>
          {(() => {
            const r = responsiveImage(PHOTOS.visionPortrait, "(max-width: 900px) 100vw, 920px");
            return (
              <img
                src={r.src}
                srcSet={r.srcSet || undefined}
                sizes={r.sizes || undefined}
                alt="DJ SPY"
                className="media-editorial-portrait"
                loading="lazy"
                decoding="async"
              />
            );
          })()}
        </div>
      </div>
    </section>
  );
}


function ThePlatform() {
  const { ui, BOOKING, PLATFORM_FEATURES } = useMediaContent();
  const features = PLATFORM_FEATURES;
  return (
    <section className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s09_platform_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s09_platform_title}</h2>
          <p className="media-lede">
            {ui.sections.s09_platform_lede_prefix}
            <a href={BOOKING.website} style={{color:'var(--media-accent)'}} dir="ltr">djspyofficial.com</a>.
          </p>
        </div>
        <div className="media-platform">
          <div>
            <div className="media-eyebrow">{ui.sections.s09_platform_features_eyebrow}</div>
            <div className="media-platform-list" style={{ marginTop: 24 }} data-testid="platform-features">
              {features.map((f, i) => (
                <div key={`${f}-${i}`} className="media-platform-feature" data-testid={`platform-feature-${i}`}>
                  <div className="dot" />
                  <div className="txt">{f}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ aspectRatio: "4/3", overflow: "hidden", border: "1px solid var(--media-line)" }}>
            {(() => {
              const r = responsiveImage(PHOTOS.boothPortrait, "(max-width: 900px) 100vw, 45vw");
              return (
                <img
                  src={r.src}
                  srcSet={r.srcSet || undefined}
                  sizes={r.sizes || undefined}
                  alt="DJ SPY"
                  className="media-editorial-portrait"
                  loading="lazy"
                  decoding="async"
                />
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}


function Recognition() {
  const { ui, RECOGNITION } = useMediaContent();
  return (
    <section id="recognition" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s10_recognition_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s10_recognition_title}</h2>
        </div>
        <div className="media-recognition" data-testid="recognition">
          {RECOGNITION.map((r) => (
            <div key={r.title} className="media-recognition-card">
              <div className="title">{r.title}</div>
              <div className="copy">{r.copy}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function VerifiedPresence() {
  const { ui, PLATFORMS } = useMediaContent();
  const track = useTrack();
  return (
    <section className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s11_verified_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s11_verified_title}</h2>
          <p className="media-lede">{ui.sections.s11_verified_lede}</p>
        </div>
        <div className="media-verified" data-testid="verified-platforms">
          {PLATFORMS.map((p) => (
            <a key={p.name}
               href={p.url}
               target="_blank"
               rel="noopener noreferrer"
               aria-label={`Open DJ SPY on ${p.name} in a new tab`}
               onClick={() => track("social_link_click", { platform: p.name })}
               data-testid={`platform-${p.name.toLowerCase().replace(/ /g,'-')}`}>
              <div className="media-verified-icon" aria-hidden="true">
                <PlatformIcon slug={p.icon_hint || p.platform_slug || p.name.toLowerCase().replace(/ /g, "_")} size={22} />
              </div>
              <div className="platform" dir="ltr">{p.name}</div>
              <div className="handle" dir="ltr">{p.handle}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}


function PressCoverage() {
  const { ui, PRESS_ARTICLES } = useMediaContent();
  const track = useTrack();
  return (
    <section id="press" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s12_press_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s12_press_title}</h2>
          <p className="media-lede">{ui.sections.s12_press_lede}</p>
        </div>
        <div className="media-press" data-testid="press">
          {PRESS_ARTICLES.map((a, i) => (
            <a key={i}
               href={a.url}
               target="_blank"
               rel="noopener noreferrer"
               aria-label={`Read: ${a.headline} — ${a.publication}`}
               onClick={() => track("press_article_click", { publication: a.publication, url: a.url })}
               className="media-press-card" data-testid={`press-${i}`}>
              <div className="pub">{a.publication}</div>
              <div className="headline">{a.headline}</div>
              <div className="meta">
                <span>{a.date}</span>
                <span className="read">{ui.sections.s12_press_read}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}


function Gallery() {
  const { ui, GALLERY } = useMediaContent();
  const track = useTrack();
  const [tab, setTab] = useState(GALLERY[0].category);
  const [lightbox, setLightbox] = useState(null);
  const active = GALLERY.find((g) => g.category === tab) || GALLERY[0];
  const catLabel = (c) => ui.sections.s13_gallery_categories[c] || c;
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);
  return (
    <section id="gallery" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s13_gallery_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s13_gallery_title}</h2>
          <p className="media-lede">{ui.sections.s13_gallery_lede}</p>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 32, flexWrap: "wrap" }} data-testid="gallery-tabs">
          {GALLERY.map((g) => (
            <button key={g.category}
                    onClick={() => setTab(g.category)}
                    data-testid={`gallery-tab-${g.category.replace(/ /g,'-').toLowerCase()}`}
                    className="media-btn"
                    style={{ background: tab === g.category ? "var(--media-ink)" : "transparent",
                             color:    tab === g.category ? "#050505" : "var(--media-ink)" }}>
              {catLabel(g.category)}
            </button>
          ))}
        </div>
        <div className="media-gallery" data-testid="gallery-grid">
          {active.items.map((it, i) => {
            const r = responsiveImage(it.src, "(max-width: 900px) 50vw, 33vw");
            return (
              <button key={i} className="media-gallery-item"
                      onClick={() => { setLightbox(it); track("photo_lightbox_open", { alt: it.alt }); }}
                      aria-label={`View photo: ${it.alt}`}
                      data-testid={`gallery-item-${i}`}>
                <img
                  src={r.src}
                  srcSet={r.srcSet || undefined}
                  sizes={r.sizes || undefined}
                  alt={it.alt}
                  loading="lazy"
                  decoding="async"
                />
              </button>
            );
          })}
        </div>
      </div>
      {lightbox && (
        <div className="media-lightbox" onClick={() => setLightbox(null)}
             role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="gallery-lightbox">
          <img src={lightbox.src} alt={lightbox.alt} decoding="async" />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
                  aria-label="Close photo viewer"
                  className="media-lightbox-close">
            ✕
          </button>
        </div>
      )}
    </section>
  );
}


function DownloadCenter() {
  const { ui, DOWNLOADS, DOWNLOAD_USAGE_NOTICE } = useMediaContent();
  const track = useTrack();
  return (
    <section id="downloads" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s14_downloads_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s14_downloads_title}</h2>
          <p className="media-lede">{ui.sections.s14_downloads_lede}</p>
        </div>
        <div className="media-downloads" data-testid="downloads">
          {DOWNLOADS.map((d) => (
            d.available ? (
              <a key={d.key} href={d.href} download={d.filename}
                 className="media-download-btn"
                 data-testid={`download-${d.key}`}
                 aria-label={`Download ${d.label}`}
                 onClick={() => track("asset_download", { asset: d.key })}>
                <div>
                  <div className="label">{d.label}</div>
                  <div className="note">
                    {d.note}
                    {d.en_only && ui.englishBadge && (
                      <span className="media-en-badge" data-testid={`en-badge-${d.key}`}>
                        {ui.englishBadge}
                      </span>
                    )}
                  </div>
                </div>
                <div className="arrow">↓</div>
              </a>
            ) : (
              <div key={d.key} className="media-download-btn"
                   data-testid={`download-${d.key}-coming-soon`}
                   aria-disabled="true"
                   style={{ opacity: 0.55, cursor: "not-allowed" }}>
                <div>
                  <div className="label">{d.label}</div>
                  <div className="note">
                    {d.note}
                    {d.en_only && ui.englishBadge && (
                      <span className="media-en-badge">{ui.englishBadge}</span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 10, letterSpacing: '0.3em', color: 'var(--media-accent)', textTransform: 'uppercase' }}>{ui.labels.comingSoon}</div>
              </div>
            )
          ))}
        </div>
        <p style={{ marginTop: 32, color: "var(--media-mute)", fontSize: 13, lineHeight: 1.6, maxWidth: 820 }} data-testid="download-usage-notice">
          {DOWNLOAD_USAGE_NOTICE}
        </p>
      </div>
    </section>
  );
}


function LatestNews() {
  const { ui, lang } = useMediaContent();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    // Backend endpoint is `/api/blog` (not `/api/blog/posts`) — using the
    // wrong path silently returned nothing on production, hiding Latest News.
    api.get("/blog?published=true&limit=6").then((r) => {
      if (!cancelled) setPosts(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!posts.length) return null;
  const blogHref = (slug) => (lang === "ar" ? `/ar/blog/${slug}` : `/blog/${slug}`);
  return (
    <section id="news" className="media-section">
      <div className="media-shell">
        <div className="media-section-head">
          <div className="media-eyebrow">{ui.sections.s15_news_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s15_news_title}</h2>
          <p className="media-lede">{ui.sections.s15_news_lede}</p>
        </div>
        <div className="media-news-grid" data-testid="latest-news">
          {posts.slice(0, 6).map((p) => (
            <a key={p.slug || p.id} href={blogHref(p.slug || p.id)} className="media-news-card" data-testid={`news-${p.slug || p.id}`}>
              <div className="thumb">
                {p.cover_url ? <img src={p.cover_url} alt={p.title} loading="lazy" decoding="async" /> : null}
              </div>
              <div className="body">
                <div className="date">{p.published_at ? new Date(p.published_at).toLocaleDateString(lang === "ar" ? "ar-EG" : undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}</div>
                <h3>{p.title}</h3>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}


function Bookings() {
  const { ui, BOOKING, BOOKING_CHANNELS } = useMediaContent();
  const track = useTrack();
  const empty = {
    name: "", company: "", email: "", phone: "",
    event_date: "", city: "", venue: "", event_type: "",
    attendance: "", duration: "", budget: "", message: "",
    consent: false,
    website_hp: "",  // honeypot
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => {
    const v = k === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e2) => (e2[k] ? { ...e2, [k]: undefined } : e2));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = ui.booking.err_name;
    if (!form.email.trim() && !form.phone.trim()) errs.email = ui.booking.err_contact;
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = ui.booking.err_email;
    if (!form.event_date.trim()) errs.event_date = ui.booking.err_date;
    if (!form.city.trim())       errs.city = ui.booking.err_city;
    if (!form.message.trim())    errs.message = ui.booking.err_message;
    if (!form.consent)           errs.consent = ui.booking.err_consent;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending || done) return;
    if (form.website_hp.trim()) { setDone(true); return; }
    if (!validate()) return;
    setSending(true);
    track("booking_started");
    try {
      const { consent, website_hp, ...payload } = form;
      await api.post("/media/booking", payload);
      setDone(true);
      track("booking_submitted");
      toast.success(ui.booking.toast_ok);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : ui.booking.toast_err);
    } finally {
      setSending(false);
    }
  };

  const errLine = (k) => errors[k] ? (<div style={{fontSize:11, color:'#ff6b6b', letterSpacing:'0.04em', marginTop:2}} role="alert">{errors[k]}</div>) : null;

  return (
    <section id="bookings" style={{ padding: "48px 0" }}>
      <div className="media-shell">
        <div className="media-section-head" style={{ paddingTop: 64 }}>
          <div className="media-eyebrow">{ui.sections.s16_bookings_eyebrow}</div>
          <h2 className="media-display">{ui.sections.s16_bookings_title}</h2>
          <p className="media-lede">{ui.sections.s16_bookings_lede}</p>
        </div>
        <div className="media-booking" style={{ borderTop: "none", padding: 0 }}>
          {done ? (
            <div data-testid="booking-thankyou" className="media-booking-form" style={{padding:32, border:'1px solid var(--media-accent)'}}>
              <div className="media-eyebrow">{ui.booking.thanks_eyebrow}</div>
              <h3 className="media-display" style={{fontSize:32, marginTop:12}}>{ui.booking.thanks_title}</h3>
              <p style={{color:'var(--media-mute)', marginTop:12, lineHeight:1.6}}>
                {ui.booking.thanks_copy}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="media-booking-form" data-testid="booking-form" noValidate>
              <input type="text" name="website" value={form.website_hp}
                     onChange={update("website_hp")} tabIndex={-1} autoComplete="off"
                     style={{position:'absolute', insetInlineStart:'-10000px', width:1, height:1, opacity:0}}
                     aria-hidden="true" />
              <label>
                <input required autoComplete="name" placeholder={ui.booking.ph_name} value={form.name}
                       onChange={update("name")} data-testid="booking-name"
                       aria-invalid={!!errors.name} aria-label={ui.booking.ph_name} />
                {errLine("name")}
              </label>
              <input autoComplete="organization" placeholder={ui.booking.ph_company} value={form.company}
                     onChange={update("company")} data-testid="booking-company" aria-label={ui.booking.ph_company} />
              <label>
                <input type="email" inputMode="email" autoComplete="email" placeholder={ui.booking.ph_email} value={form.email}
                       onChange={update("email")} data-testid="booking-email"
                       aria-invalid={!!errors.email} aria-label={ui.booking.ph_email} />
                {errLine("email")}
              </label>
              <input type="tel" inputMode="tel" autoComplete="tel" placeholder={ui.booking.ph_phone} value={form.phone}
                     onChange={update("phone")} data-testid="booking-phone" aria-label={ui.booking.ph_phone} />
              <label>
                <input required type="date" placeholder={ui.booking.ph_date} value={form.event_date}
                       onChange={update("event_date")} data-testid="booking-date"
                       aria-invalid={!!errors.event_date} aria-label={ui.booking.ph_date} />
                {errLine("event_date")}
              </label>
              <label>
                <input required placeholder={ui.booking.ph_city} value={form.city}
                       onChange={update("city")} data-testid="booking-city"
                       aria-invalid={!!errors.city} aria-label={ui.booking.ph_city} />
                {errLine("city")}
              </label>
              <input placeholder={ui.booking.ph_venue} value={form.venue}
                     onChange={update("venue")} data-testid="booking-venue" aria-label={ui.booking.ph_venue} />
              <input placeholder={ui.booking.ph_event_type} value={form.event_type}
                     onChange={update("event_type")} data-testid="booking-event-type" aria-label={ui.booking.ph_event_type} />
              <input inputMode="numeric" placeholder={ui.booking.ph_attendance} value={form.attendance}
                     onChange={update("attendance")} data-testid="booking-attendance" aria-label={ui.booking.ph_attendance} />
              <input placeholder={ui.booking.ph_duration} value={form.duration}
                     onChange={update("duration")} data-testid="booking-duration" aria-label={ui.booking.ph_duration} />
              <input placeholder={ui.booking.ph_budget} value={form.budget}
                     onChange={update("budget")} data-testid="booking-budget" aria-label={ui.booking.ph_budget} />
              <label>
                <textarea required placeholder={ui.booking.ph_message}
                          value={form.message} onChange={update("message")} data-testid="booking-message"
                          aria-invalid={!!errors.message} aria-label={ui.booking.ph_message} />
                {errLine("message")}
              </label>
              <label className="media-consent-row" data-testid="booking-consent-row">
                <input type="checkbox" checked={form.consent} onChange={update("consent")}
                       data-testid="booking-consent" aria-label={ui.booking.consent} />
                <span>{ui.booking.consent}</span>
              </label>
              {errLine("consent")}
              <button type="submit" disabled={sending}
                      className="media-btn media-btn--accent" data-testid="booking-submit"
                      style={{ alignSelf: "flex-start", padding: "16px 32px", marginTop: 4 }}>
                {sending ? ui.booking.submitting : ui.booking.submit}
              </button>
            </form>
          )}
          <div className="media-booking-details" data-testid="booking-details">
            <div className="media-eyebrow">{ui.labels.directChannels}</div>
            <a href={`mailto:${BOOKING.email}`} onClick={() => track("email_click")}
               aria-label={ui.labels.email} data-testid="booking-channel-email">
              <span className="label">{ui.labels.email}</span><span dir="ltr">{BOOKING.email}</span>
            </a>
            {BOOKING_CHANNELS.map((ch) => {
              const key = (ch.name || "").toLowerCase();
              const analyticsPlatform = ch.name || "channel";
              // Website gets its own tracking event to preserve
              // pre-refactor analytics granularity.
              const onClick = () => {
                if (key === "website") track("website_click");
                else track("social_link_click", { platform: analyticsPlatform });
              };
              const aria = `Open DJ SPY on ${ch.name} in a new tab`;
              // Match the label-slot mapping used pre-refactor so
              // Instagram / TikTok / Telegram / Website all render the
              // same localised label as before.
              const labelSlot = ui.labels[key] || ch.name;
              return (
                <a key={ch.name} href={ch.url} target="_blank" rel="noopener noreferrer"
                   onClick={onClick} aria-label={aria}
                   data-testid={`booking-channel-${key.replace(/\s+/g, "-")}`}>
                  <span className="label">{labelSlot}</span>
                  <span dir="ltr">{ch.handle || ch.url}</span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}


function Footer() {
  const { ui, isAr } = useMediaContent();
  return (
    <footer className="media-footer" data-testid="media-footer">
      {isAr && ui.copyright ? (
        <>{ui.footerLine} · {ui.copyright}</>
      ) : (
        <>{ui.footerLine} · © {new Date().getFullYear()}</>
      )}
    </footer>
  );
}


// Structured data + head-injected SEO. English `/media` is
// index,follow. Arabic `/ar/media` renders as noindex,follow with a
// canonical → /media until the DJ SPY team approves the translated
// copy — this matches the guardrail in `LocaleContext.jsx`.
const PRODUCTION_ORIGIN = "https://djspyofficial.com";
function StructuredData() {
  const { isAr, GENRES, BIO_PARAGRAPHS, PLATFORMS } = useMediaContent();
  const person = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Johnny Eid",
    "alternateName": "DJ SPY",
    "nationality": "Lebanese",
    "url": `${PRODUCTION_ORIGIN}${isAr ? "/ar/media" : "/media"}`,
    "image": PHOTOS.heroPortrait,
    "sameAs": PLATFORMS.filter((p) => p.name !== "Website").map((p) => p.url),
    "jobTitle": "DJ and Music Producer",
    "worksFor": { "@type": "Organization", "name": "DJSPYOfficial", "url": PRODUCTION_ORIGIN },
    "homeLocation": { "@type": "Place", "name": "Dubai, United Arab Emirates" },
    "birthPlace": { "@type": "Place", "name": "Lebanon" },
    "award": ["YouTube Silver Creator Award"],
    "knowsAbout": GENRES,
    "description": BIO_PARAGRAPHS[0],
    "inLanguage": isAr ? "ar" : "en",
  }), [isAr, GENRES, BIO_PARAGRAPHS, PLATFORMS]);
  const breadcrumb = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1,
        "name": isAr ? "DJ SPY Official" : "DJ SPY Official",
        "item": `${PRODUCTION_ORIGIN}${isAr ? "/ar" : "/"}` },
      { "@type": "ListItem", "position": 2,
        "name": isAr ? "المركز الإعلامي" : "Media Center",
        "item": `${PRODUCTION_ORIGIN}${isAr ? "/ar/media" : "/media"}` },
    ],
  }), [isAr]);
  useEffect(() => {
    const injected = [];
    const setMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        injected.push(el);
      }
      el.setAttribute("content", value);
    };
    const title = isAr
      ? "المركز الإعلامي الرسمي لـ DJ SPY | السيرة والصور والملف الصحفي"
      : "DJ SPY Official Media Center | Biography, Press Kit & Photos";
    const description = isAr
      ? "المركز الإعلامي الرسمي لـ DJ SPY، ويضم السيرة الذاتية، والصور الصحفية، والملف الصحفي، وأبرز العروض، والتغطيات الإعلامية، ومعلومات الحجز والتواصل."
      : "Official media center for DJ SPY, Lebanese DJ and music producer based in Dubai. Explore his biography, career highlights, press coverage, official photos, platform links and international booking information.";

    document.title = title;
    setMeta("name", "description", description);

    // Robots / canonical. Arabic stays noindex,follow until copy approval
    // — the LocaleContext also enforces this at a higher level; we set
    // it explicitly here so this page always self-manages its head.
    if (isAr) {
      setMeta("name", "robots", "index,follow");
    } else {
      setMeta("name", "robots", "index,follow");
    }

    // Open Graph
    setMeta("property", "og:type", "profile");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", `${PRODUCTION_ORIGIN}${isAr ? "/ar/media" : "/media"}`);
    setMeta("property", "og:image", PHOTOS.heroPortrait);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:site_name", "DJ SPY Official");
    setMeta("property", "og:locale", isAr ? "ar_AR" : "en_US");
    // Alternate locale hint for OG-consuming crawlers.
    setMeta("property", "og:locale:alternate", isAr ? "en_US" : "ar_AR");

    // Twitter / X
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", PHOTOS.heroPortrait);

    // Canonical: each locale is now self-canonical (post copy approval).
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.rel = "canonical";
      document.head.appendChild(canon);
      injected.push(canon);
    }
    canon.href = `${PRODUCTION_ORIGIN}${isAr ? "/ar/media" : "/media"}`;

    // Reciprocal hreflang set — both routes advertise EN, AR and
    // x-default. Clean up any stale alternates first so a page
    // transition from EN ↔ AR doesn't leave doubled links behind.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    const addAlt = (hl, href) => {
      const a = document.createElement("link");
      a.rel = "alternate";
      a.hreflang = hl;
      a.href = href;
      document.head.appendChild(a);
      injected.push(a);
    };
    addAlt("en",        `${PRODUCTION_ORIGIN}/media`);
    addAlt("ar",        `${PRODUCTION_ORIGIN}/ar/media`);
    addAlt("x-default", `${PRODUCTION_ORIGIN}/media`);

    // Preload the hero image so LCP lands fast — same file for both
    // locales because the photograph is language-neutral.
    const HERO_PRELOAD_ID = "media-hero-preload";
    let preload = document.head.querySelector(`link#${HERO_PRELOAD_ID}`);
    if (!preload) {
      const hp = heroPreloadAttrs();
      preload = document.createElement("link");
      preload.id = HERO_PRELOAD_ID;
      preload.rel = "preload";
      preload.as = "image";
      preload.setAttribute("fetchpriority", "high");
      preload.setAttribute("imagesrcset", hp.imagesrcset);
      preload.setAttribute("imagesizes", hp.imagesizes);
      preload.href = hp.href;
      document.head.appendChild(preload);
      injected.push(preload);
    }

    // JSON-LD blocks
    const ldPerson = document.createElement("script");
    ldPerson.type = "application/ld+json";
    ldPerson.text = JSON.stringify(person);
    document.head.appendChild(ldPerson); injected.push(ldPerson);
    const ldBreadcrumb = document.createElement("script");
    ldBreadcrumb.type = "application/ld+json";
    ldBreadcrumb.text = JSON.stringify(breadcrumb);
    document.head.appendChild(ldBreadcrumb); injected.push(ldBreadcrumb);

    return () => { injected.forEach((el) => el.remove()); };
  }, [person, breadcrumb, isAr]);
  return null;
}


export default function MediaCenter() {
  const { isAr } = useMediaContent();
  useEffect(() => {
    // Fire a locale-aware page view when the component mounts.
    try {
      api.post("/log/client", {
        event: "media_page_view",
        context: { locale: isAr ? "ar" : "en" },
        url: window.location.href,
        ua: navigator.userAgent,
        ts: new Date().toISOString(),
      }).catch(() => {});
    } catch (_) { /* analytics never blocks render */ }
  }, [isAr]);
  return (
    <div className={`media-root${isAr ? " media-root--ar" : ""}`} data-testid="media-center">
      <StructuredData />
      <MediaNav />
      <Hero />
      <Biography />
      <CareerTimeline />
      <ByTheNumbers />
      <InternationalPresence />
      <PerformedAlongside />
      <TheMusic />
      <TheVision />
      <ThePlatform />
      <Recognition />
      <VerifiedPresence />
      <PressCoverage />
      <Gallery />
      <DownloadCenter />
      <LatestNews />
      <Bookings />
      <Footer />
    </div>
  );
}
